// Secret wrapping (docs/03 §2, §7): secrets (identity seed, refresh token) are
// never stored raw. Wrapping is delegated to a pluggable `SecretCipher` so the
// platform decides where the wrapping key lives:
//   - web/iOS (default): a non-extractable AES-GCM-256 WebCrypto CryptoKey kept
//     in storage (CryptoKeys are structured-cloneable into IndexedDB but their
//     material is unreadable from JS);
//   - Android (J11): hardware Keystore via the MerkurySecureStorage Capacitor
//     plugin (a CryptoKey cannot cross the JSON-only bridge) — see
//     src/shell/capacitor/.
// The stored record shape ({iv, ciphertext} under `secret:<name>`) and the
// WebCrypto key's own storage slot are unchanged from the pre-seam layout, so
// existing installs keep decrypting.

import { randomBytes } from '../lib/bytes.js';
import type { KvStore } from './kv.js';

const WRAPPING_KEY_KEY = 'wrapping-key';
const SECRET_PREFIX = 'secret:';
const GCM_IV_LENGTH = 12;

export interface WrappedSecret {
  iv: Uint8Array;
  ciphertext: Uint8Array;
}

/** Platform seam: how secrets are wrapped at rest. */
export interface SecretCipher {
  /** Optional one-time setup (e.g. create the wrapping key); awaited by open(). */
  init?(): Promise<void>;
  wrap(secret: Uint8Array): Promise<WrappedSecret>;
  unwrap(wrapped: WrappedSecret): Promise<Uint8Array>;
}

/**
 * Default cipher: non-extractable WebCrypto AES-GCM key persisted in the same
 * KV store, under the same slot the pre-seam SecretStore used.
 */
export class WebCryptoSecretCipher implements SecretCipher {
  private key: CryptoKey | undefined;

  constructor(private readonly kv: KvStore) {}

  async init(): Promise<void> {
    await this.wrappingKey();
  }

  private async wrappingKey(): Promise<CryptoKey> {
    if (this.key) return this.key;
    let key = await this.kv.get<CryptoKey>(WRAPPING_KEY_KEY);
    if (!key) {
      key = await crypto.subtle.generateKey({ name: 'AES-GCM', length: 256 }, false, [
        'encrypt',
        'decrypt',
      ]);
      await this.kv.put(WRAPPING_KEY_KEY, key);
    }
    this.key = key;
    return key;
  }

  async wrap(secret: Uint8Array): Promise<WrappedSecret> {
    const key = await this.wrappingKey();
    const iv = randomBytes(GCM_IV_LENGTH);
    const ciphertext = new Uint8Array(
      await crypto.subtle.encrypt(
        { name: 'AES-GCM', iv: iv as BufferSource },
        key,
        secret as BufferSource,
      ),
    );
    return { iv, ciphertext };
  }

  async unwrap(wrapped: WrappedSecret): Promise<Uint8Array> {
    const key = await this.wrappingKey();
    return new Uint8Array(
      await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv: wrapped.iv as BufferSource },
        key,
        wrapped.ciphertext as BufferSource,
      ),
    );
  }
}

export class SecretStore {
  private constructor(
    private readonly kv: KvStore,
    private readonly cipher: SecretCipher,
  ) {}

  /** Opens with the given cipher, defaulting to the WebCrypto implementation. */
  static async open(kv: KvStore, cipher?: SecretCipher): Promise<SecretStore> {
    const chosen = cipher ?? new WebCryptoSecretCipher(kv);
    await chosen.init?.();
    return new SecretStore(kv, chosen);
  }

  async put(name: string, secret: Uint8Array): Promise<void> {
    const wrapped = await this.cipher.wrap(secret);
    await this.kv.put(SECRET_PREFIX + name, wrapped);
  }

  /** Returns undefined when absent; throws if the blob fails authentication. */
  async get(name: string): Promise<Uint8Array | undefined> {
    const wrapped = await this.kv.get<WrappedSecret>(SECRET_PREFIX + name);
    if (!wrapped) return undefined;
    return this.cipher.unwrap(wrapped);
  }

  async delete(name: string): Promise<void> {
    await this.kv.delete(SECRET_PREFIX + name);
  }
}
